import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useI18n from "../i18n/useI18n";

const THEME_KEY = "hookora_theme";

const publicToolLinks = [
  { labelKey: "tools.viralHookGenerator", fallback: "Viral Hook Generator", to: "/viral-hook-generator" },
  { labelKey: "tools.youtubeTitleOptimizer", fallback: "YouTube Title Optimizer", to: "/youtube-title-optimizer" },
  { labelKey: "tools.captionGenerator", fallback: "Caption Generator", to: "/caption-generator" },
  { labelKey: "tools.hashtagGenerator", fallback: "Hashtag Generator", to: "/hashtag-generator" },
  { labelKey: "tools.descriptionRewriter", fallback: "Description Rewriter", to: "/description-rewriter" },
  { labelKey: "tools.shortScriptGenerator", fallback: "Short Script Generator", to: "/short-script-generator" },
  { labelKey: "tools.thumbnailTextAnalyzer", fallback: "Thumbnail Text Analyzer", to: "/thumbnail-text-analyzer" }
];

const studioOnlyTools = [
  { key: "tools.hookAngleGenerator", fallback: "Hook Angle Generator" },
  { key: "tools.ctaGenerator", fallback: "CTA Generator" },
  { key: "tools.aiHumanizer", fallback: "AI Humanizer" },
  { key: "tools.contentRepurposer", fallback: "Content Repurposer" },
  { key: "tools.adCopyGenerator", fallback: "Ad Copy Generator" },
  { key: "tools.carouselGenerator", fallback: "Carousel Generator" },
  { key: "tools.viralRewriter", fallback: "Viral Rewriter" },
  { key: "tools.offerPositioning", fallback: "Offer Positioning" },
  { key: "tools.hookScore", fallback: "Hook Score" },
  { key: "tools.trendToContent", fallback: "Trend to Content" },
  { key: "tools.nigerianAudienceRewrite", fallback: "Nigerian Audience Rewrite" },
  { key: "tools.retentionIntroGenerator", fallback: "Retention Intro Generator" },
  { key: "tools.brandVoiceTrainer", fallback: "Brand Voice Trainer" },
  { key: "tools.contentDifferentiation", fallback: "Content Differentiation" },
  { key: "tools.commentToContent", fallback: "Comment to Content" }
];

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language, setLanguage } = useI18n();

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const getStoredToken = () => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem("access_token");
  };

  const getStoredUser = () => {
    if (typeof window === "undefined") return null;

    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const [token, setToken] = useState(getStoredToken());
  const [user, setUser] = useState(getStoredUser());
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [leftMenuOpen, setLeftMenuOpen] = useState(false);
  const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 900 : false
  );
  const [theme, setTheme] = useState(
    typeof window !== "undefined" ? localStorage.getItem(THEME_KEY) || "light" : "light"
  );
  const [isFullscreen, setIsFullscreen] = useState(
    typeof document !== "undefined" ? Boolean(document.fullscreenElement) : false
  );

  const mobileMenuRef = useRef(null);
  const mobileButtonRef = useRef(null);
  const leftMenuRef = useRef(null);
  const leftMenuButtonRef = useRef(null);
  const toolsMenuRef = useRef(null);
  const toolsButtonRef = useRef(null);

  const isAdmin = Boolean(user?.is_admin);
  const isLoggedIn = Boolean(token);
  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      navBg: isDark ? "rgba(2, 6, 23, 0.88)" : "rgba(255, 255, 255, 0.92)",
      navBorder: isDark ? "rgba(148, 163, 184, 0.16)" : "#e5e7eb",
      navShadow: isDark
        ? "0 10px 30px rgba(2, 6, 23, 0.38)"
        : "0 8px 28px rgba(15, 23, 42, 0.05)",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#cbd5e1" : "#4b5563",
      pillBg: isDark ? "rgba(30, 41, 59, 0.95)" : "#f3f4f6",
      pillText: isDark ? "#cbd5e1" : "#6b7280",
      activeBg: isDark ? "rgba(59, 130, 246, 0.16)" : "#e5e7eb",
      activeText: isDark ? "#f8fafc" : "#111827",
      buttonBg: isDark ? "#0f172a" : "#ffffff",
      buttonBorder: isDark ? "rgba(148, 163, 184, 0.24)" : "#d1d5db",
      menuBg: isDark ? "rgba(15, 23, 42, 0.97)" : "#ffffff",
      menuBorder: isDark ? "rgba(148, 163, 184, 0.18)" : "#e5e7eb",
      menuShadow: isDark
        ? "0 24px 50px rgba(2, 6, 23, 0.55)"
        : "0 20px 40px rgba(15, 23, 42, 0.14)",
      surface: isDark ? "rgba(30, 41, 59, 0.84)" : "#f8fafc",
      surfaceBorder: isDark ? "rgba(148, 163, 184, 0.16)" : "#e5e7eb",
      gradientText: isDark
        ? "linear-gradient(180deg, #f8fafc 0%, #cbd5e1 100%)"
        : "linear-gradient(180deg, #111827 0%, #374151 100%)",
      logoBg: isDark
        ? "linear-gradient(135deg, #3b82f6, #8b5cf6)"
        : "linear-gradient(135deg, #2563eb, #7c3aed)",
      settingsTitleBg: isDark ? "rgba(30,41,59,0.88)" : "#f8fafc",
      settingsTitleBorder: isDark ? "rgba(148,163,184,0.16)" : "#e5e7eb",
      settingsSectionLabel: isDark ? "#e2e8f0" : "#111827",
      settingsSectionMuted: isDark ? "#94a3b8" : "#64748b",
      settingsChipBg: isDark ? "rgba(15,23,42,0.9)" : "#ffffff",
      settingsChipBorder: isDark ? "rgba(148,163,184,0.18)" : "#dbe2ea",
      settingsSelectedBg: isDark
        ? "linear-gradient(135deg, rgba(29,78,216,0.22), rgba(124,58,237,0.22))"
        : "linear-gradient(135deg, #111827, #374151)",
      settingsSelectedBorder: isDark ? "rgba(96,165,250,0.42)" : "#111827",
      settingsSelectedText: "#ffffff",
      studioChipBg: isDark ? "rgba(30,41,59,0.72)" : "#f8fafc",
      studioChipBorder: isDark ? "rgba(148,163,184,0.14)" : "#e5e7eb",
      studioChipText: isDark ? "#cbd5e1" : "#475569",
      ctaBg: "linear-gradient(135deg, #2563eb, #7c3aed)",
      ctaText: "#ffffff"
    }),
    [isDark]
  );

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleResize = () => {
      const mobile = window.innerWidth < 900;
      setIsMobile(mobile);

      if (!mobile) {
        setMobileMenuOpen(false);
        setMobileToolsOpen(false);
      } else {
        setToolsMenuOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
    setLeftMenuOpen(false);
    setToolsMenuOpen(false);
    setMobileToolsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncAuthState = () => {
      setToken(getStoredToken());
      setUser(getStoredUser());
    };

    const syncPreferences = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    const handleStorage = () => {
      syncAuthState();
      syncPreferences();
    };

    const handleAuthChanged = () => syncAuthState();
    const handlePreferencesChanged = () => syncPreferences();

    window.addEventListener("storage", handleStorage);
    window.addEventListener("hookora-auth-updated", handleAuthChanged);
    window.addEventListener("hookora-preferences-updated", handlePreferencesChanged);
    window.addEventListener("hookora-theme-change", handlePreferencesChanged);

    return () => {
      window.removeEventListener("storage", handleStorage);
      window.removeEventListener("hookora-auth-updated", handleAuthChanged);
      window.removeEventListener("hookora-preferences-updated", handlePreferencesChanged);
      window.removeEventListener("hookora-theme-change", handlePreferencesChanged);
    };
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuOpen) {
        const clickedOutsideMenu =
          mobileMenuRef.current && !mobileMenuRef.current.contains(event.target);
        const clickedOutsideButton =
          mobileButtonRef.current && !mobileButtonRef.current.contains(event.target);

        if (clickedOutsideMenu && clickedOutsideButton) {
          setMobileMenuOpen(false);
          setMobileToolsOpen(false);
        }
      }

      if (leftMenuOpen) {
        const clickedOutsideLeftMenu =
          leftMenuRef.current && !leftMenuRef.current.contains(event.target);
        const clickedOutsideLeftButton =
          leftMenuButtonRef.current && !leftMenuButtonRef.current.contains(event.target);

        if (clickedOutsideLeftMenu && clickedOutsideLeftButton) {
          setLeftMenuOpen(false);
        }
      }

      if (toolsMenuOpen) {
        const clickedOutsideToolsMenu =
          toolsMenuRef.current && !toolsMenuRef.current.contains(event.target);
        const clickedOutsideToolsButton =
          toolsButtonRef.current && !toolsButtonRef.current.contains(event.target);

        if (clickedOutsideToolsMenu && clickedOutsideToolsButton) {
          setToolsMenuOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [mobileMenuOpen, leftMenuOpen, toolsMenuOpen]);

  const handleLogout = () => {
    if (typeof window !== "undefined") {
      localStorage.removeItem("access_token");
      localStorage.removeItem("user");
      window.dispatchEvent(new CustomEvent("hookora-auth-updated"));
    }

    navigate("/login");
  };

  const setPreferenceTheme = (value) => {
    if (typeof window === "undefined") return;
    setTheme(value);
    localStorage.setItem(THEME_KEY, value);
    window.dispatchEvent(new CustomEvent("hookora-preferences-updated"));
    window.dispatchEvent(new CustomEvent("hookora-theme-change"));
  };

  const setPreferenceLanguage = (value) => {
    setLanguage(value);
  };

  const toggleFullscreen = async () => {
    try {
      if (typeof document === "undefined") return;

      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error("Fullscreen toggle failed:", error);
    }
  };

  const isActivePath = (path) => location.pathname === path;
  const isToolsActive = publicToolLinks.some((item) => location.pathname === item.to);

  const linkStyle = (path) => ({
    color: isActivePath(path) ? colors.activeText : colors.textMuted,
    textDecoration: "none",
    fontWeight: isActivePath(path) ? "700" : "500",
    padding: "10px 14px",
    borderRadius: "10px",
    background: isActivePath(path) ? colors.activeBg : "transparent",
    transition: "all 0.2s ease",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "auto",
    boxSizing: "border-box",
    whiteSpace: "nowrap"
  });

  const mobileLinkStyle = (path) => ({
    ...linkStyle(path),
    width: "100%",
    justifyContent: "flex-end"
  });

  const toolsButtonStyle = {
    color: isToolsActive || toolsMenuOpen ? colors.activeText : colors.textMuted,
    background: isToolsActive || toolsMenuOpen ? colors.activeBg : "transparent",
    border: "none",
    fontWeight: isToolsActive ? "700" : "500",
    padding: "10px 14px",
    borderRadius: "10px",
    transition: "all 0.2s ease",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    cursor: "pointer",
    whiteSpace: "nowrap"
  };

  const planBadgeStyle = useMemo(() => {
    if (user?.plan === "creator") {
      return {
        color: "#1d4ed8",
        background: isDark ? "rgba(29,78,216,0.18)" : "#dbeafe",
        border: `1px solid ${isDark ? "rgba(147,197,253,0.34)" : "#93c5fd"}`
      };
    }

    if (user?.plan === "pro") {
      return {
        color: "#8b5cf6",
        background: isDark ? "rgba(109,40,217,0.18)" : "#f3e8ff",
        border: `1px solid ${isDark ? "rgba(216,180,254,0.34)" : "#d8b4fe"}`
      };
    }

    return {
      color: isDark ? "#fbbf24" : "#92400e",
      background: isDark ? "rgba(245,158,11,0.18)" : "#fef3c7",
      border: `1px solid ${isDark ? "rgba(252,211,77,0.28)" : "#fcd34d"}`
    };
  }, [user?.plan, isDark]);

  const displayPlanLabel =
    user?.plan === "creator"
      ? tt("plans.creatorBadge", "✔ Creator")
      : user?.plan === "pro"
      ? tt("plans.proBadge", "✔ Pro")
      : tt("plans.freeBadge", "Free");

  const languageLabel = tt(`languages.${language}`, language.toUpperCase());

  const desktopAuthLinks = (
    <>
      <span
        style={{
          color: colors.pillText,
          fontSize: "14px",
          background: colors.pillBg,
          padding: "8px 12px",
          borderRadius: "999px",
          textAlign: "center",
          border: isDark ? `1px solid ${colors.menuBorder}` : "none"
        }}
      >
        {user?.username ? `${tt("navbar.hi", "Hi")}, ${user.username}` : tt("navbar.loggedIn", "Logged in")}
      </span>

      {user?.plan && (
        <span
          style={{
            ...planBadgeStyle,
            fontSize: "12px",
            fontWeight: "700",
            padding: "7px 11px",
            borderRadius: "999px",
            textTransform: "none",
            width: "fit-content"
          }}
        >
          {displayPlanLabel}
        </span>
      )}

      <Link to="/dashboard" style={linkStyle("/dashboard")}>
        {tt("common.dashboard", "Dashboard")}
      </Link>

      <Link to="/studio" style={linkStyle("/studio")}>
        {tt("common.studio", "Studio")}
      </Link>

      <Link to="/library" style={linkStyle("/library")}>
        {tt("common.library", "Library")}
      </Link>

      <Link to="/app/pricing" style={linkStyle("/app/pricing")}>
        {tt("common.billing", "Billing")}
      </Link>

      {isAdmin && (
        <Link to="/admin/waitlist" style={linkStyle("/admin/waitlist")}>
          {tt("common.waitlistAdmin", "Waitlist Admin")}
        </Link>
      )}

      <button
        onClick={handleLogout}
        style={{
          background: "#ef4444",
          color: "white",
          border: "none",
          borderRadius: "10px",
          padding: "10px 14px",
          cursor: "pointer",
          fontWeight: "600"
        }}
      >
        {tt("common.logout", "Logout")}
      </button>
    </>
  );

  const desktopGuestLinks = (
    <>
      <Link to="/" style={linkStyle("/")}>
        {tt("common.home", "Home")}
      </Link>
      <Link to="/features" style={linkStyle("/features")}>
        {tt("common.features", "Features")}
      </Link>
      <Link to="/pricing" style={linkStyle("/pricing")}>
        {tt("common.pricing", "Pricing")}
      </Link>
      <Link to="/about" style={linkStyle("/about")}>
        {tt("common.about", "About")}
      </Link>
      <Link to="/blog" style={linkStyle("/blog")}>
        {tt("common.blog", "Blog")}
      </Link>

      <div style={{ position: "relative" }}>
        <button
          ref={toolsButtonRef}
          onClick={() => setToolsMenuOpen((prev) => !prev)}
          style={toolsButtonStyle}
        >
          {tt("common.tools", "Tools")}
          <span style={{ fontSize: "12px" }}>{toolsMenuOpen ? "▲" : "▼"}</span>
        </button>

        {toolsMenuOpen && (
          <div
            ref={toolsMenuRef}
            style={{
              position: "absolute",
              top: "48px",
              left: 0,
              width: "320px",
              maxHeight: "460px",
              overflowY: "auto",
              display: "grid",
              gap: "10px",
              background: colors.menuBg,
              border: `1px solid ${colors.menuBorder}`,
              borderRadius: "16px",
              padding: "12px",
              boxShadow: colors.menuShadow,
              zIndex: 45,
              backdropFilter: "blur(18px)"
            }}
          >
            <div
              style={{
                color: colors.text,
                fontWeight: "800",
                fontSize: "13px",
                padding: "4px 4px 2px"
              }}
            >
              {tt("navbar.publicToolPages", "Public tool pages")}
            </div>

            {publicToolLinks.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                style={{
                  color: location.pathname === item.to ? colors.activeText : colors.textMuted,
                  textDecoration: "none",
                  fontWeight: location.pathname === item.to ? "700" : "500",
                  padding: "10px 12px",
                  borderRadius: "10px",
                  background: location.pathname === item.to ? colors.activeBg : "transparent",
                  whiteSpace: "nowrap"
                }}
              >
                {tt(item.labelKey, item.fallback)}
              </Link>
            ))}

            <div
              style={{
                marginTop: "4px",
                paddingTop: "12px",
                borderTop: `1px solid ${colors.menuBorder}`
              }}
            >
              <div
                style={{
                  color: colors.text,
                  fontWeight: "800",
                  fontSize: "13px",
                  marginBottom: "10px"
                }}
              >
                {tt("navbar.moreToolsInStudio", "More tools available inside Studio")}
              </div>

              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px"
                }}
              >
                {studioOnlyTools.map((tool) => (
                  <span
                    key={tool.key}
                    style={{
                      padding: "8px 10px",
                      borderRadius: "999px",
                      background: colors.studioChipBg,
                      border: `1px solid ${colors.studioChipBorder}`,
                      color: colors.studioChipText,
                      fontSize: "12px",
                      fontWeight: "600",
                      lineHeight: 1.4
                    }}
                  >
                    {tt(tool.key, tool.fallback)}
                  </span>
                ))}
              </div>

              <Link
                to="/register"
                style={{
                  marginTop: "12px",
                  display: "inline-flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "100%",
                  textDecoration: "none",
                  background: colors.ctaBg,
                  color: colors.ctaText,
                  padding: "12px 14px",
                  borderRadius: "12px",
                  fontWeight: "800",
                  boxSizing: "border-box"
                }}
              >
                {tt("common.unlockAllStudioTools", "Unlock all Studio tools")}
              </Link>
            </div>
          </div>
        )}
      </div>

      <Link to="/login" style={linkStyle("/login")}>
        {tt("common.login", "Login")}
      </Link>
      <Link to="/register" style={linkStyle("/register")}>
        {tt("common.register", "Register")}
      </Link>
    </>
  );

  const mobileAuthLinks = (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-end",
          gap: "8px",
          width: "100%",
          paddingBottom: "8px",
          borderBottom: `1px solid ${colors.menuBorder}`
        }}
      >
        <span
          style={{
            color: colors.pillText,
            fontSize: "14px",
            background: colors.pillBg,
            padding: "8px 12px",
            borderRadius: "999px",
            textAlign: "center",
            border: isDark ? `1px solid ${colors.menuBorder}` : "none"
          }}
        >
          {user?.username ? `${tt("navbar.hi", "Hi")}, ${user.username}` : tt("navbar.loggedIn", "Logged in")}
        </span>

        {user?.plan && (
          <span
            style={{
              ...planBadgeStyle,
              fontSize: "12px",
              fontWeight: "700",
              padding: "7px 11px",
              borderRadius: "999px",
              textTransform: "none"
            }}
          >
            {displayPlanLabel}
          </span>
        )}
      </div>

      <Link to="/dashboard" style={mobileLinkStyle("/dashboard")}>
        {tt("common.dashboard", "Dashboard")}
      </Link>
      <Link to="/studio" style={mobileLinkStyle("/studio")}>
        {tt("common.studio", "Studio")}
      </Link>
      <Link to="/library" style={mobileLinkStyle("/library")}>
        {tt("common.library", "Library")}
      </Link>
      <Link to="/app/pricing" style={mobileLinkStyle("/app/pricing")}>
        {tt("common.billing", "Billing")}
      </Link>
      <Link to="/settings" style={mobileLinkStyle("/settings")}>
        {tt("common.settings", "Account Settings")}
      </Link>

      {isAdmin && (
        <Link to="/admin/waitlist" style={mobileLinkStyle("/admin/waitlist")}>
          {tt("common.waitlistAdmin", "Waitlist Admin")}
        </Link>
      )}

      <button
        onClick={handleLogout}
        style={{
          background: "#ef4444",
          color: "white",
          border: "none",
          borderRadius: "10px",
          padding: "10px 14px",
          cursor: "pointer",
          fontWeight: "600",
          minWidth: "120px"
        }}
      >
        {tt("common.logout", "Logout")}
      </button>
    </>
  );

  const mobileGuestLinks = (
    <>
      <Link to="/" style={mobileLinkStyle("/")}>
        {tt("common.home", "Home")}
      </Link>
      <Link to="/features" style={mobileLinkStyle("/features")}>
        {tt("common.features", "Features")}
      </Link>
      <Link to="/pricing" style={mobileLinkStyle("/pricing")}>
        {tt("common.pricing", "Pricing")}
      </Link>
      <Link to="/about" style={mobileLinkStyle("/about")}>
        {tt("common.about", "About")}
      </Link>
      <Link to="/blog" style={mobileLinkStyle("/blog")}>
        {tt("common.blog", "Blog")}
      </Link>

      <button
        onClick={() => setMobileToolsOpen((prev) => !prev)}
        style={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          gap: "8px",
          color: isToolsActive || mobileToolsOpen ? colors.activeText : colors.textMuted,
          background: isToolsActive || mobileToolsOpen ? colors.activeBg : "transparent",
          border: "none",
          borderRadius: "10px",
          padding: "10px 14px",
          fontWeight: isToolsActive ? "700" : "500",
          cursor: "pointer"
        }}
      >
        {tt("common.tools", "Tools")}
        <span style={{ fontSize: "12px" }}>{mobileToolsOpen ? "▲" : "▼"}</span>
      </button>

      {mobileToolsOpen && (
        <div
          style={{
            width: "100%",
            display: "grid",
            gap: "10px",
            padding: "4px 0 8px",
            maxHeight: "380px",
            overflowY: "auto"
          }}
        >
          <div
            style={{
              color: colors.text,
              fontWeight: "800",
              fontSize: "13px",
              textAlign: "right"
            }}
          >
            {tt("navbar.publicToolPages", "Public tool pages")}
          </div>

          {publicToolLinks.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              style={{
                color: location.pathname === item.to ? colors.activeText : colors.textMuted,
                textDecoration: "none",
                fontWeight: location.pathname === item.to ? "700" : "500",
                padding: "10px 14px",
                borderRadius: "10px",
                background: location.pathname === item.to ? colors.activeBg : colors.surface,
                border: `1px solid ${colors.surfaceBorder}`,
                width: "100%",
                textAlign: "right",
                boxSizing: "border-box"
              }}
            >
              {tt(item.labelKey, item.fallback)}
            </Link>
          ))}

          <div
            style={{
              paddingTop: "10px",
              borderTop: `1px solid ${colors.menuBorder}`
            }}
          >
            <div
              style={{
                color: colors.text,
                fontWeight: "800",
                fontSize: "13px",
                marginBottom: "10px",
                textAlign: "right"
              }}
            >
              {tt("navbar.moreToolsInStudio", "More tools available inside Studio")}
            </div>

            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                justifyContent: "flex-end"
              }}
            >
              {studioOnlyTools.map((tool) => (
                <span
                  key={tool.key}
                  style={{
                    padding: "8px 10px",
                    borderRadius: "999px",
                    background: colors.studioChipBg,
                    border: `1px solid ${colors.studioChipBorder}`,
                    color: colors.studioChipText,
                    fontSize: "12px",
                    fontWeight: "600",
                    lineHeight: 1.4
                  }}
                >
                  {tt(tool.key, tool.fallback)}
                </span>
              ))}
            </div>

            <Link
              to="/register"
              style={{
                marginTop: "12px",
                width: "100%",
                display: "inline-flex",
                justifyContent: "center",
                textDecoration: "none",
                background: colors.ctaBg,
                color: colors.ctaText,
                padding: "12px 14px",
                borderRadius: "12px",
                fontWeight: "800",
                boxSizing: "border-box"
              }}
            >
              {tt("common.unlockAllStudioTools", "Unlock all Studio tools")}
            </Link>
          </div>
        </div>
      )}

      <Link to="/login" style={mobileLinkStyle("/login")}>
        {tt("common.login", "Login")}
      </Link>
      <Link to="/register" style={mobileLinkStyle("/register")}>
        {tt("common.register", "Register")}
      </Link>
    </>
  );

  return (
    <div
      style={{
        background: colors.navBg,
        borderBottom: `1px solid ${colors.navBorder}`,
        padding: isMobile ? "14px 16px" : "14px 24px",
        position: "sticky",
        top: 0,
        zIndex: 30,
        backdropFilter: "blur(16px)",
        boxShadow: colors.navShadow
      }}
    >
      <div
        style={{
          maxWidth: "1280px",
          margin: "0 auto",
          position: "relative"
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "44px 1fr auto",
            alignItems: "center",
            gap: "14px"
          }}
        >
          <div style={{ display: "flex", justifyContent: "flex-start" }}>
            <button
              ref={leftMenuButtonRef}
              onClick={() => setLeftMenuOpen((prev) => !prev)}
              aria-label={tt("navbar.openSettingsMenu", "Open settings menu")}
              style={{
                border: `1px solid ${colors.buttonBorder}`,
                background: colors.buttonBg,
                color: colors.text,
                borderRadius: "12px",
                width: "44px",
                height: "44px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "22px",
                fontWeight: "700",
                flexShrink: 0,
                boxShadow: leftMenuOpen ? colors.navShadow : "none"
              }}
            >
              ⋯
            </button>
          </div>

          <div style={{ display: "flex", justifyContent: "center", minWidth: 0 }}>
            <Link
              to={isLoggedIn ? "/dashboard" : "/"}
              style={{
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: isMobile ? "0" : "12px",
                minWidth: 0
              }}
            >
              {!isMobile && (
  <img
    src="/hookora-logo.png"
    alt="Hookora"
    style={{
      width: "42px",
      height: "42px",
      objectFit: "contain",
      flexShrink: 0,
      filter: isDark
        ? "drop-shadow(0 10px 24px rgba(59,130,246,0.22))"
        : "drop-shadow(0 8px 18px rgba(37,99,235,0.16))"
    }}
  />
)}

              <div
                style={{
                  fontSize: isMobile ? "22px" : "25px",
                  fontWeight: "900",
                  letterSpacing: "-0.045em",
                  lineHeight: 1,
                  fontFamily: "Inter, ui-sans-serif, system-ui, -apple-system, sans-serif",
                  background: colors.gradientText,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textRendering: "geometricPrecision",
                  whiteSpace: "nowrap"
                }}
              >
                Hookora
              </div>
            </Link>
          </div>

          {isMobile ? (
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                ref={mobileButtonRef}
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                aria-label={tt("navbar.toggleNavigationMenu", "Toggle navigation menu")}
                style={{
                  border: `1px solid ${colors.buttonBorder}`,
                  background: colors.buttonBg,
                  color: colors.text,
                  borderRadius: "12px",
                  width: "44px",
                  height: "44px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "20px",
                  flexShrink: 0,
                  boxShadow: mobileMenuOpen ? colors.navShadow : "none"
                }}
              >
                {mobileMenuOpen ? "✕" : "☰"}
              </button>
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "6px",
                flexWrap: "wrap"
              }}
            >
              {isLoggedIn ? desktopAuthLinks : desktopGuestLinks}
            </div>
          )}
        </div>

        {leftMenuOpen && (
          <div
            ref={leftMenuRef}
            style={{
              position: "absolute",
              top: "58px",
              left: 0,
              width: "min(320px, calc(100vw - 32px))",
              display: "flex",
              flexDirection: "column",
              gap: "12px",
              background: colors.menuBg,
              border: `1px solid ${colors.menuBorder}`,
              borderRadius: "18px",
              padding: "14px",
              boxShadow: colors.menuShadow,
              zIndex: 40,
              backdropFilter: "blur(18px)"
            }}
          >
            {isLoggedIn && (
              <Link
                to="/settings"
                style={{
                  color: colors.text,
                  textDecoration: "none",
                  fontWeight: "700",
                  padding: "11px 12px",
                  borderRadius: "12px",
                  background: colors.settingsTitleBg,
                  border: `1px solid ${colors.settingsTitleBorder}`,
                  display: "flex",
                  alignItems: "center",
                  gap: "10px"
                }}
              >
                <span style={{ fontSize: "15px" }}>⚙️</span>
                <span>{tt("common.settings", "Account Settings")}</span>
              </Link>
            )}

            <button
              type="button"
              onClick={toggleFullscreen}
              style={{
                color: colors.text,
                fontWeight: "700",
                padding: "11px 12px",
                borderRadius: "12px",
                background: colors.settingsTitleBg,
                border: `1px solid ${colors.settingsTitleBorder}`,
                display: "flex",
                alignItems: "center",
                gap: "10px",
                cursor: "pointer"
              }}
            >
              <span style={{ fontSize: "15px" }}>{isFullscreen ? "🗗" : "⛶"}</span>
              <span>
                {isFullscreen
                  ? tt("common.fullscreenExit", "Exit Fullscreen")
                  : tt("common.fullscreenEnter", "Enter Fullscreen")}
              </span>
            </button>

            <div
              style={{
                paddingTop: "8px",
                borderTop: `1px solid ${colors.menuBorder}`
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: "700",
                  color: colors.settingsSectionLabel,
                  fontSize: "13px",
                  marginBottom: "10px"
                }}
              >
                <span style={{ fontSize: "14px" }}>{theme === "dark" ? "🌙" : "☀️"}</span>
                <span>{tt("common.theme", "Theme")}</span>
              </div>

              <div style={{ display: "flex", gap: "10px" }}>
                <button
                  onClick={() => setPreferenceTheme("light")}
                  title={tt("common.light", "Light")}
                  aria-label={tt("common.light", "Light")}
                  style={themeToggleStyle({
                    selected: theme === "light",
                    colors,
                    isDark
                  })}
                >
                  ☀️
                </button>

                <button
                  onClick={() => setPreferenceTheme("dark")}
                  title={tt("common.dark", "Dark")}
                  aria-label={tt("common.dark", "Dark")}
                  style={themeToggleStyle({
                    selected: theme === "dark",
                    colors,
                    isDark
                  })}
                >
                  🌙
                </button>
              </div>
            </div>

            <div
              style={{
                paddingTop: "8px",
                borderTop: `1px solid ${colors.menuBorder}`
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontWeight: "700",
                  color: colors.settingsSectionLabel,
                  fontSize: "13px",
                  marginBottom: "6px"
                }}
              >
                <span style={{ fontSize: "14px" }}>🌐</span>
                <span>{tt("common.language", "Language")}</span>
              </div>

              <div
                style={{
                  color: colors.settingsSectionMuted,
                  fontSize: "12px",
                  marginBottom: "10px"
                }}
              >
                {tt("common.current", "Current")}: {languageLabel}
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap"
                }}
              >
                <button
                  onClick={() => setPreferenceLanguage("en")}
                  style={languageChipStyle({
                    selected: language === "en",
                    colors,
                    isDark
                  })}
                >
                  EN
                </button>

                <button
                  onClick={() => setPreferenceLanguage("es")}
                  style={languageChipStyle({
                    selected: language === "es",
                    colors,
                    isDark
                  })}
                >
                  ES
                </button>

                <button
                  onClick={() => setPreferenceLanguage("fr")}
                  style={languageChipStyle({
                    selected: language === "fr",
                    colors,
                    isDark
                  })}
                >
                  FR
                </button>

                <button
                  onClick={() => setPreferenceLanguage("pt")}
                  style={languageChipStyle({
                    selected: language === "pt",
                    colors,
                    isDark
                  })}
                >
                  PT
                </button>
              </div>
            </div>
          </div>
        )}

        {isMobile && mobileMenuOpen && (
          <div
            ref={mobileMenuRef}
            style={{
              position: "absolute",
              top: "58px",
              right: 0,
              width: "min(320px, calc(100vw - 32px))",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "10px",
              background: colors.menuBg,
              border: `1px solid ${colors.menuBorder}`,
              borderRadius: "18px",
              padding: "14px",
              boxShadow: colors.menuShadow,
              zIndex: 40,
              backdropFilter: "blur(18px)"
            }}
          >
            {isLoggedIn ? mobileAuthLinks : mobileGuestLinks}
          </div>
        )}
      </div>
    </div>
  );
};

const themeToggleStyle = ({ selected, colors, isDark }) => ({
  width: "46px",
  height: "42px",
  borderRadius: "12px",
  border: `1px solid ${selected ? colors.settingsSelectedBorder : colors.settingsChipBorder}`,
  background: selected ? colors.settingsSelectedBg : colors.settingsChipBg,
  color: selected ? colors.settingsSelectedText : colors.text,
  fontSize: "18px",
  fontWeight: "700",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: selected
    ? isDark
      ? "0 10px 22px rgba(59,130,246,0.24)"
      : "0 6px 16px rgba(17,24,39,0.14)"
    : "none",
  transition: "all 0.2s ease"
});

const languageChipStyle = ({ selected, colors, isDark }) => ({
  minWidth: "44px",
  height: "38px",
  padding: "0 12px",
  borderRadius: "12px",
  border: `1px solid ${selected ? colors.settingsSelectedBorder : colors.settingsChipBorder}`,
  background: selected ? colors.settingsSelectedBg : colors.settingsChipBg,
  color: selected ? colors.settingsSelectedText : colors.text,
  fontWeight: "700",
  cursor: "pointer",
  boxShadow: selected
    ? isDark
      ? "0 10px 22px rgba(59,130,246,0.24)"
      : "0 6px 16px rgba(17,24,39,0.14)"
    : "none",
  transition: "all 0.2s ease"
});

export default Navbar;